import React, { useCallback, useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../../theme';
import {
    ExclamationMarkTriangle24,
    ExclamationMarkCircle24,
    CheckCircle24,
    InfoCircle24,
    Close16
} from '@honeybook/icons';
import { ISystemToastProps, IToastAction, ToastType } from '../HBToast.types';
import HBText from '../../HBText/HBText';
import { mediumUp } from '../../../styles/breakpoints';
import { dissolve, fromDirection } from '../../../styles/animations';
import ToastActions from '../ToastActions';
import ToastActionLink from '../ToastActionLink';
import {
    aiInlineFillBackground,
    aiAccentLineGradient,
    aiLayeredBoxShadow
} from '../../../styles/mixins';
import HBIndicatorAI from '../../HBIndicatorAI/HBIndicatorAI';
import spacing from '../../../styles/spacing';

export const DEFAULT_DISMISS_MS = 5000;
export const CONTAINER_TEST_ID = 'hb-toast--container';
export const SYSTEM_TOAST_CLOSE_BUTTON_TEST_ID =
    'hb-system-toast-close--button';

const DEFAULT_ICONS: Record<
    Exclude<ToastType, ToastType.MemberToast | ToastType.CustomInfo>,
    (props: unknown) => JSX.Element
> = {
    [ToastType.Error]: ExclamationMarkTriangle24,
    [ToastType.Success]: CheckCircle24,
    [ToastType.Info]: InfoCircle24,
    [ToastType.Warning]: ExclamationMarkCircle24,
    [ToastType.ai]: () => <HBIndicatorAI size="sm" />
};

export const SystemToast = React.memo(
    ({
        iconColor,
        id,
        type,
        title,
        text,
        isDismissible = true,
        autoDismissMs = isDismissible ? DEFAULT_DISMISS_MS : 0,
        onDismiss,
        removeItem,
        icon: Icon = DEFAULT_ICONS[type],
        actions,
        actionLink,
        customColor,
        layout = 'column'
    }: ISystemToastProps) => {
        const [isVisible, setIsVisible] = useState(true);
        const [isRendered, setIsRendered] = useState(true);
        const [isHovered, setIsHovered] = useState(false);

        const timeoutRef = useRef<NodeJS.Timeout>();

        const onAnimationEnd = () => {
            if (!isVisible) {
                setIsRendered(false);
                removeItem(id);
            }
        };

        const dismiss = useCallback(() => {
            setIsVisible(false);
            onDismiss?.();
        }, [onDismiss]);

        const onActionClick = (action: IToastAction) => {
            action?.onClick();
            dismiss();
        };

        useEffect(() => {
            if (
                typeof autoDismissMs !== 'number' ||
                !autoDismissMs ||
                !isVisible ||
                isHovered
            ) {
                return;
            }

            timeoutRef.current = setTimeout(dismiss, autoDismissMs);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }, [isDismissible, dismiss, autoDismissMs, isVisible, isHovered]);

        if (!isRendered) {
            return null;
        }

        const isAiTheme = type === ToastType.ai;

        return (
            <SystemToastContainer
                isVisible={isVisible}
                isAiTheme={isAiTheme}
                onAnimationEnd={onAnimationEnd}
                data-testid={CONTAINER_TEST_ID}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Bar
                    type={type}
                    customColor={customColor}
                    isAiTheme={isAiTheme}
                />
                <ContentContainer>
                    {Icon && (
                        <Icon color={iconColor || vars.colors.text.secondary} />
                    )}
                    <ContentWrapper layout={layout} type={type}>
                        <TextWrapper>
                            {typeof title === 'string' ? (
                                <HBText
                                    type="emphasis14"
                                    color="text.primary"
                                    testId="hb-toast--title"
                                >
                                    {title}
                                </HBText>
                            ) : (
                                title
                            )}
                            {text ? (
                                <ToastText
                                    type="body14"
                                    testId="hb-toast--text"
                                >
                                    {text}
                                </ToastText>
                            ) : null}
                        </TextWrapper>

                        {actions && (
                            <ActionsWrapper>
                                <ToastActions
                                    actions={actions}
                                    onActionClick={onActionClick}
                                />
                            </ActionsWrapper>
                        )}

                        {actionLink && (
                            <ToastActionLink
                                actionLink={actionLink}
                                onActionClick={onActionClick}
                            />
                        )}
                    </ContentWrapper>
                    {isDismissible && (
                        <StyledXIcon
                            data-testid={SYSTEM_TOAST_CLOSE_BUTTON_TEST_ID}
                            color={vars.colors.text.secondary}
                            onClick={dismiss}
                        />
                    )}
                </ContentContainer>
            </SystemToastContainer>
        );
    }
);

// -------------------------------- Styles -------------------------------- //
function getAnimation({ isVisible }: { isVisible: boolean }) {
    return isVisible
        ? css`
              animation: ${fromDirection('right')} 0.7s;
          `
        : css`
              animation: ${dissolve('up')} 0.7s;
          `;
}

function barColorByType(type: ToastType, customColor = null) {
    switch (type) {
        case ToastType.Error:
            return vars.colors.base.danger;
        case ToastType.Success:
            return vars.colors.base.success;
        case ToastType.Info:
            return vars.colors.base.accent;
        case ToastType.Warning:
            return vars.colors.base.warning;
        case ToastType.CustomInfo:
            return customColor;
        case ToastType.ai:
            return 'transparent';
        default:
            return vars.colors.text.primary;
    }
}

export const SystemToastContainer = styled.div<{
    isVisible: boolean;
    isAiTheme?: boolean;
}>`
    display: flex;
    position: relative;
    width: 100%;
    background-color: ${vars.colors.surface.container};
    border-radius: ${vars.radius.xs};
    box-shadow: ${vars.shadow['40']};
    overflow: hidden;
    transition: transform 0.6s ease-in-out, opacity 400ms,
        max-height 0.6s ease-in-out, margin-bottom 0.6s ease-in-out;
    ${getAnimation}

    ${({ isVisible }) =>
        isVisible
            ? css`
                  max-height: 400px;
                  margin-bottom: 8px;
              `
            : css`
                  max-height: 0;
                  margin-bottom: 0;
              `}

    ${mediumUp} {
        min-width: 320px;
        max-width: 480px;
    }
    ${({ isAiTheme }) =>
        isAiTheme &&
        css`
            ${aiInlineFillBackground};
            ${aiLayeredBoxShadow};
        `}
`;

const Bar = styled.div<{
    type: ToastType;
    customColor?: string;
    isAiTheme?: boolean;
}>`
    width: 4px;
    margin: 4px 0 4px 4px;
    border-radius: ${vars.radius.full};
    background-color: ${props => barColorByType(props.type, props.customColor)};
    ${({ isAiTheme }) =>
        isAiTheme &&
        css`
            background: ${aiAccentLineGradient};
        `}
`;

const ContentContainer = styled.div`
    padding: 16px 20px 16px 10px;
    display: flex;
    width: 100%;
`;

const ContentWrapper = styled.div<ISystemToastProps>`
    width: 100%;
    margin-left: 10px;
    display: flex;
    flex-direction: ${props => props.layout};
    align-items: ${props => (props.layout === 'column' ? 'normal' : 'center')};
    justify-content: space-between;
`;

const StyledXIcon = styled(Close16)`
    margin-inline-start: 12px;
    margin-top: 6px;
`;

const ToastText = styled(HBText)`
    padding-right: 28px;
    white-space: pre-line;
`;

const ActionsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[4]};
`;
